// Shopping Cart Page
import React from 'react';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, Flower, Droplet, Shapes, Flame, Sparkles } from 'lucide-react';

export default function CartPage() {
  const { getCartItems, getCartTotal, getCartCount, updateQuantity, removeFromCart } = useCart();
  const cartItems = getCartItems();
  const total = getCartTotal();
  const count = getCartCount();

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Flowers":
        return Flower;
      case "Soaps":
        return Droplet;
      case "Ceramic & Pottery":
        return Shapes;
      case "Candles":
        return Flame;
      case "Special Edition":
        return Sparkles;
      default:
        return Sparkles;
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="h-12 w-12 text-primary-foreground" />
          </div>
          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Your Cart is Empty
          </h2>
          <p className="text-muted-foreground mb-6">
            Start adding some beautiful items to your cart!
          </p>
          <Button
            onClick={() => window.location.hash = '#/products'}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30"
          >
            Browse Products
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
              <ShoppingCart className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Shopping Cart
          </h1>
          <p className="text-xl text-muted-foreground">
            {count} {count === 1 ? 'item' : 'items'} in your cart
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card
                key={item.id}
                className="overflow-hidden backdrop-blur-sm bg-card/70 border-2 border-border"
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Product Image Placeholder */}
                    <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-secondary/60 to-accent/40 flex items-center justify-center flex-shrink-0">
                      {(() => {
                        const Icon = getCategoryIcon(item.category);
                        return <Icon className="h-10 w-10 text-primary/50" />;
                      })()}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-1">{item.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        {item.category} {item.subCategory && `• ${item.subCategory}`}
                      </p>
                      <p className="text-xl font-bold text-primary">
                        ${item.price.toFixed(2)}
                      </p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex flex-col items-end justify-between">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="h-8 w-8 p-0"
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-12 text-center font-semibold">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="h-8 w-8 p-0"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>

                      <p className="text-sm font-semibold">
                        Subtotal: ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6 backdrop-blur-sm bg-card/70 border-2 border-border">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-semibold">${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="font-semibold">Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="font-semibold">Calculated at checkout</span>
                  </div>
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-primary">
                        ${total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => window.location.hash = '#/checkout'}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg py-6 shadow-lg shadow-primary/30"
                >
                  Proceed to Checkout
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                <Button
                  variant="outline"
                  onClick={() => window.location.hash = '#/products'}
                  className="w-full"
                >
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
